import { Injectable, TemplateRef, signal } from '@angular/core';

/**
 * Lets the active page project its own menu/controls into the app header's
 * free center space (between the version text and the user panel).
 *
 * Usage: declare an `<ng-template #headerMenu>` in the page, register it via
 * `set()` in ngAfterViewInit, and release it via `clear()` in ngOnDestroy.
 * The header renders whatever template is currently registered.
 *
 * The header row is a fixed height (DevExtreme's toolbar pins it at 36px), so
 * a menu that wraps spills over the page rather than growing the header. A
 * page whose menu may not fit compares its one-line width with `slotWidth`
 * and renders the template in its own layout instead (revenue-fees;
 * combined-orders, whose menu changes with the data, re-measures it and
 * shortens it in stages first).
 */
@Injectable({
    providedIn: 'root',
})
export class HeaderContentService {
    private readonly contentSignal = signal<TemplateRef<unknown> | null>(null);
    readonly content = this.contentSignal.asReadonly();

    // Width of the center slot, published by the header as it resizes. It
    // does not depend on what the slot holds — the slot stretches between the
    // fixed before/after sections — so it is valid with or without a menu.
    private readonly slotWidthSignal = signal(0);
    readonly slotWidth = this.slotWidthSignal.asReadonly();

    set(template: TemplateRef<unknown>): void {
        this.contentSignal.set(template);
    }

    // Clear only when this template is still the active one — a page being
    // destroyed must not wipe the menu the next page just registered.
    clear(template: TemplateRef<unknown>): void {
        if (this.contentSignal() === template) {
            this.contentSignal.set(null);
        }
    }

    setSlotWidth(width: number): void {
        this.slotWidthSignal.set(width);
    }
}
